import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { LiveMap } from "@/components/LiveMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = { pending: "Order received", assigned: "Rider assigned", picked_up: "Picked up", out_for_delivery: "Out for delivery", delivered: "Delivered", failed: "Failed" };
const STEPS = ["pending", "assigned", "picked_up", "out_for_delivery", "delivered"];

export default function TrackDelivery() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [name, setName] = useState("Customer");
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/track/${token}`); if (!r.ok) throw new Error();
      setD(await r.json());
      const m = await fetch(`${API}/public/track/${token}/messages`); setMsgs(m.ok ? await m.json() : []);
    } catch { setErr(true); }
  }, [token]);
  useEffect(() => { load(); const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [load]);

  const send = async () => {
    if (!text.trim()) return;
    await fetch(`${API}/public/track/${token}/messages`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, name }) });
    setText(""); load();
  };
  const upload = async () => {
    if (!file) return toast.error("Choose a prescription image");
    const fd = new FormData(); fd.append("file", file); fd.append("patient_name", name);
    const r = await fetch(`${API}/public/track/${token}/prescription`, { method: "POST", body: fd });
    if (r.ok) { toast.success("Prescription uploaded — pending verification"); setFile(null); } else toast.error("Upload failed");
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500">Tracking link not found.</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  const activeIdx = STEPS.indexOf(d.status);
  const trackMarkers = [];
  if (d.rider_location) trackMarkers.push({ lat: d.rider_location.latitude, lng: d.rider_location.longitude, color: "#2563eb", label: `Rider${d.rider_name ? ": " + d.rider_name : ""}` });
  if (d.dest_lat != null) trackMarkers.push({ lat: d.dest_lat, lng: d.dest_lng, color: "#e11d48", label: "Delivery address" });

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <Toaster position="top-right" richColors />
      <div className="max-w-lg mx-auto">
        <div className="flex justify-center mb-6"><img src="/mobimed-logo.png" alt="MobiMed" className="h-16 object-contain" /></div>
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-1">
            <h1 className="font-bold text-lg text-slate-800">Order {d.invoice_no}</h1>
            <span className="text-xs font-semibold text-sky-700">{LABEL[d.status]}</span>
          </div>
          <p className="text-sm text-slate-500 mb-5">{d.customer_name} · {d.address || "—"}{d.rider_name ? ` · Rider: ${d.rider_name}` : ""}</p>
          <ol className="space-y-3 mb-2">
            {STEPS.map((s, i) => (
              <li key={s} className="flex items-center gap-3" data-testid={`track-step-${s}`}>
                <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold ${i <= activeIdx ? "bg-sky-600 text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</span>
                <span className={`text-sm ${i <= activeIdx ? "text-slate-800 font-medium" : "text-slate-400"}`}>{LABEL[s]}</span>
              </li>
            ))}
          </ol>
          {d.pod && (
            <div className="mt-3 border-t border-slate-100 pt-3" data-testid="track-pod">
              <div className="text-xs font-semibold text-emerald-700 mb-1">Proof of delivery</div>
              <div className="text-sm text-slate-700">Received by {d.pod.recipient_name}{d.pod.relationship ? ` (${d.pod.relationship})` : ""}</div>
              {d.pod.note && <div className="text-xs text-slate-500 mt-0.5">{d.pod.note}</div>}
              {d.pod.photo_path && <img src={`${API}/public/track/${token}/pod-photo`} alt="Proof of delivery" data-testid="track-pod-photo" onError={(e) => { e.currentTarget.style.display = "none"; }} className="mt-2 rounded-lg border border-slate-200 w-full max-h-56 object-cover" />}
              {d.pod.latitude && d.pod.longitude && <a href={`https://www.google.com/maps?q=${d.pod.latitude},${d.pod.longitude}`} target="_blank" rel="noreferrer" data-testid="track-pod-map" className="inline-block mt-2 text-xs text-sky-700 underline">View delivery location on map</a>}
            </div>
          )}
        </div>

        {trackMarkers.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-3 mt-4" data-testid="track-map">
            <LiveMap markers={trackMarkers} height={260} />
            <div className="flex items-center gap-4 px-1 pt-2 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#2563eb" }} /> Rider</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#e11d48" }} /> Your address</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm p-6 mt-4">
          <h2 className="font-semibold text-slate-800 mb-2">Upload a prescription</h2>
          <input type="file" accept="image/*,.pdf" data-testid="track-rx-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm mb-2" />
          <button data-testid="track-rx-upload" onClick={upload} className="w-full bg-sky-600 text-white rounded-lg py-2 text-sm font-medium">Upload prescription</button>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 mt-4">
          <h2 className="font-semibold text-slate-800 mb-2">Chat with the pharmacy & rider</h2>
          <div className="max-h-56 overflow-y-auto space-y-2 mb-2">
            {msgs.length === 0 && <div className="text-sm text-slate-400 text-center py-4">No messages yet</div>}
            {msgs.map((m) => (
              <div key={m.id} className={`text-sm ${m.sender_role === "customer" ? "text-right" : "text-left"}`}>
                <span className="inline-block bg-slate-100 rounded-lg px-2.5 py-1.5"><span className="block text-[10px] uppercase text-slate-400">{m.sender_name} · {m.sender_role}</span>{m.text}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2"><input data-testid="track-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" /><button data-testid="track-chat-send" onClick={send} className="bg-sky-600 text-white rounded-lg px-4 text-sm font-medium">Send</button></div>
        </div>
      </div>
    </div>
  );
}
